// Extraction preview: request, progress, failure, and result (create-doc-interactive design, "PreviewPanel.tsx and
// usePreview"). Which preview is shown is decided by the pure reducer in previewFlow.ts; this file only wires it to
// the API and renders it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import LiveRegion from '@cloudscape-design/components/live-region';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import {
  allowedValuesByPath,
  PREVIEW_LABEL,
  previewView,
  SAMPLE_EXPIRED_TEXT,
  type BdaSchema,
  type Run,
} from '@bda/shared';
import { api } from '../../api/client';
import { useRun, useRunResult } from '../../api/hooks';
import { hasRole, useAuth } from '../../auth/AuthProvider';
import { safeName } from '../../lib/download';
import {
  initialPreviewState,
  mapPreviewError,
  PREVIEW_NETWORK_TEXT,
  previewReducer,
  previewStatus,
  type PreviewAction,
  type PreviewError,
  type PreviewRequest,
  type PreviewState,
  type PreviewStatus,
  type SchemaPreviewBody,
} from './previewFlow';
import { RunStatusIndicator } from '../runs/RunResultPage';
import { DocumentViewer } from '../../components/DocumentViewer';
import { ResultView } from '../runs/ResultView';

export const NO_FAILURE_REASON_TEXT = 'BDA returned no failure reason.';
export const STARTING_TEXT = 'Starting the preview.';

/** Preview state over any preview-starting call (`usePreviewOf`); `T` is the request body. */
export interface UsePreviewOf<T> {
  state: PreviewState<T>;
  status: PreviewStatus<T>;
  /** Starts a preview with `request` minus its `basisKey`; also repeats a failed request exactly (Req 2.4). */
  send: (request: PreviewRequest<T>) => void;
  /** Drops every request and the shown preview. */
  clear: () => void;
}

/** The request body `start` receives: the request without its `basisKey`. */
function bodyOf<T>(request: PreviewRequest<T>): T {
  const { basisKey: _basisKey, ...body } = request;
  return body as T;
}

/**
 * Preview state over `start` (`api.createPreview`, `api.createSetPreview`). Polls the current preview run with `useRun`
 * (same interval and stop rule as other runs, Req 2.1) and feeds every poll to the reducer, which ignores polls of
 * earlier runs (Req 3.6). A token refresh during polling is the client's own 401 path and never touches this state
 * (Req 7.6). `start` should be stable (an `api` method).
 */
export function usePreviewOf<T>(
  start: (body: T) => Promise<Run>,
  opts: { enabled: boolean; basisKey?: string },
): UsePreviewOf<T> {
  const [state, setState] = useState<PreviewState<T>>(() => ({
    nextRequestId: initialPreviewState.nextRequestId,
    attempt: null,
    shown: null,
  }));
  // The reducer runs synchronously against a ref, so a request ID is known before React renders.
  const ref = useRef(state);
  const dispatch = useCallback((a: PreviewAction<T>) => {
    ref.current = previewReducer(ref.current, a);
    setState(ref.current);
  }, []);

  const { enabled } = opts;
  const send = useCallback(
    (request: PreviewRequest<T>) => {
      if (!enabled) return;
      const requestId = ref.current.nextRequestId;
      dispatch({ type: 'request', request });
      start(bodyOf(request))
        .then((run) =>
          dispatch({
            type: 'accepted',
            requestId,
            runId: run.id,
            status: run.status,
            failureReason: run.failureReason,
          }),
        )
        .catch((e: unknown) => dispatch({ type: 'rejected', requestId, error: mapPreviewError(e) }));
    },
    [dispatch, enabled, start],
  );
  const clear = useCallback(() => dispatch({ type: 'clear' }), [dispatch]);

  const runningId = enabled && state.attempt?.phase === 'running' ? state.attempt.runId : undefined;
  const run = useRun(runningId);
  useEffect(() => {
    const r = run.data;
    if (r && r.id === runningId)
      dispatch({ type: 'runStatus', runId: r.id, status: r.status, failureReason: r.failureReason });
  }, [run.data, runningId, dispatch]);

  return { state, status: previewStatus(state, opts.basisKey ?? ''), send, clear };
}

export interface UsePreview {
  state: PreviewState;
  status: PreviewStatus;
  /** Requests a Preview_Run of the source document with `schema`; `basisKey` identifies what it was built from. */
  request: (schema: BdaSchema, basisKey: string) => void;
  /** Repeats a failed request exactly (Req 2.4). */
  retry: (request: PreviewRequest) => void;
  /** Drops every request and the shown preview (Use another document, Suggest again, Add N selected). */
  clear: () => void;
}

/** Calls `api.createPreview` at request time, so the method can be replaced (tests) after the hook mounts. */
const startExtractionPreview = (body: SchemaPreviewBody): Promise<Run> => api.createPreview(body);

/** Extraction-preview state for one Start_From_Document_Flow or Editor: `usePreviewOf(api.createPreview)`. */
export function usePreview(opts: { sourceRunId: string | null; enabled: boolean; basisKey?: string }): UsePreview {
  const { state, status, send, clear } = usePreviewOf(startExtractionPreview, opts);
  const { sourceRunId } = opts;
  const request = useCallback(
    (schema: BdaSchema, basisKey: string) => {
      if (sourceRunId) send({ sourceRunId, schema, basisKey });
    },
    [send, sourceRunId],
  );
  return { state, status, request, retry: send, clear };
}

function errorText(e: PreviewError): { header: string; lines: string[] } {
  switch (e.kind) {
    case 'validation':
      return { header: 'The fields cannot be previewed', lines: e.messages };
    case 'bda':
      return { header: 'BDA rejected the fields', lines: [e.message] };
    case 'throttled':
    case 'split':
    case 'other':
      return { header: 'The preview could not be started', lines: [e.message] };
    case 'expired':
      return { header: 'The preview could not be started', lines: [SAMPLE_EXPIRED_TEXT] };
    case 'network':
      return { header: 'The preview could not be started', lines: [PREVIEW_NETWORK_TEXT] };
  }
}

/** The shown Extraction_Preview: the document beside the Result_View in preview layout (Req 3.1, 3.2). */
function ShownPreview({
  runId,
  request,
  stale,
  staleText,
}: {
  runId: string;
  request: PreviewRequest;
  stale: boolean;
  staleText: string;
}) {
  const run = useRun(runId);
  const result = useRunResult(runId, true);
  const [goTo, setGoTo] = useState<{ page: number; seq: number } | undefined>();
  const data = result.data && !result.data.expired ? result.data : null;
  // The stored Preview_Schema when the API returns it, else the schema this panel requested.
  const schema = (data && 'schema' in data && data.schema) || request.schema;
  const view = useMemo(() => (data ? previewView(schema, data.result) : null), [schema, data]);
  const allowed = useMemo(() => allowedValuesByPath(schema), [schema]);
  const r = run.data;
  return (
    <Container
      header={
        <Header variant="h2" actions={<Badge color="blue">{PREVIEW_LABEL}</Badge>}>
          Extraction preview
        </Header>
      }
    >
      <SpaceBetween size="l">
        {stale && <Alert type="warning">{staleText}</Alert>}
        {result.error && <Alert type="error">The preview result could not be loaded.</Alert>}
        {result.data?.expired && <Alert type="info">The preview result has expired.</Alert>}
        {data && view && r ? (
          <Grid gridDefinition={[{ colspan: { default: 12, l: 5 } }, { colspan: { default: 12, l: 7 } }]}>
            <DocumentViewer url={data.documentUrl} contentType={r.contentType} name={r.fileName} goTo={goTo} />
            <ResultView
              result={data.result}
              baseName={safeName(`preview-${r.fileName}`)}
              allowed={allowed}
              preview={view}
              onShowPage={
                r.contentType === 'application/pdf'
                  ? (page) => setGoTo((g) => ({ page, seq: (g?.seq ?? 0) + 1 }))
                  : undefined
              }
            />
          </Grid>
        ) : (
          !result.error && !result.data?.expired && <Spinner />
        )}
      </SpaceBetween>
    </Container>
  );
}

/**
 * Status, rejection, and failure (with "Try again") of the most recent preview attempt; shared by `PreviewPanel` and
 * the set preview of the proposal review (blueprint-sets Req 11.4).
 */
export function PreviewProgress<T>({
  state,
  retry,
}: {
  state: PreviewState<T>;
  retry: (request: PreviewRequest<T>) => void;
}) {
  const { tryAgain } = previewStatus(state, '');
  const a = state.attempt;
  const err = a?.phase === 'rejected' ? errorText(a.error) : null;
  return (
    <>
      <LiveRegion>
        {a?.phase === 'starting' && <StatusIndicator type="loading">{STARTING_TEXT}</StatusIndicator>}
        {a?.phase === 'running' && (
          <SpaceBetween direction="horizontal" size="xs">
            <Box>Preview:</Box>
            <RunStatusIndicator status={a.status} />
          </SpaceBetween>
        )}
      </LiveRegion>
      {err && (
        <Alert type="error" header={err.header}>
          {err.lines.length === 1 ? (
            err.lines[0]
          ) : (
            <ul>
              {err.lines.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}
      {a?.phase === 'failed' && (
        <Alert
          type="error"
          header="The preview failed"
          action={tryAgain && <Button onClick={() => retry(tryAgain)}>Try again</Button>}
        >
          {a.reason || NO_FAILURE_REASON_TEXT}
        </Alert>
      )}
    </>
  );
}

/**
 * Status, failure, and result of the most recent preview. Rendered only for Blueprint_Authors (Req 6.9).
 * `basisKey` is what the current selection or fields would preview; a shown preview with another key is out of date.
 */
export function PreviewPanel({
  preview,
  basisKey,
  staleText,
  idPrefix,
}: {
  preview: UsePreview;
  basisKey: string;
  staleText: string;
  idPrefix: string;
}) {
  const { user } = useAuth();
  if (!hasRole(user, 'BlueprintAuthor')) return null;
  const { state } = preview;
  const { stale } = previewStatus(state, basisKey);
  return (
    <div id={`${idPrefix}-preview`}>
      <SpaceBetween size="m">
        <PreviewProgress state={state} retry={preview.retry} />
        {state.shown && (
          <ShownPreview
            key={state.shown.runId}
            runId={state.shown.runId}
            request={state.shown.request}
            stale={stale}
            staleText={staleText}
          />
        )}
      </SpaceBetween>
    </div>
  );
}
